/**
 * Hovering a link shows a preview of what it points to (SPEC §6.2): the title and the first rows,
 * read-only. One document-level listener for every link on screen (no state per link); shown
 * after a short pause, never on touch.
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { visibleRows, type NodeId } from '@solar/core'
import { dayTitle } from './dates.ts'
import { useStore } from './hooks.ts'
import { Content } from './render.tsx'

const DELAY = 450
const ROWS = 10

export function HoverPreview() {
  const store = useStore()
  const [at, setAt] = useState<{ id: NodeId; x: number; y: number } | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inside = useRef(false)

  useEffect(() => {
    if (matchMedia('(hover: none)').matches) return
    const target = (el: EventTarget | null): { id: NodeId; rect: DOMRect } | null => {
      const a = el instanceof Element ? el.closest('a.ref, a.tag, a.mention, a.blockref') : null
      if (!a || a.closest('.preview')) return null
      const id = a.getAttribute('data-block') ?? (a.getAttribute('data-ref') ? store.titles.resolve(a.getAttribute('data-ref')!) : null)
      return id ? { id, rect: a.getBoundingClientRect() } : null
    }
    const over = (e: MouseEvent): void => {
      const t = target(e.target)
      if (!t) return
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => setAt({ id: t.id, x: t.rect.left, y: t.rect.bottom + 6 }), DELAY)
    }
    const out = (e: MouseEvent): void => {
      if (!target(e.target)) return
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => !inside.current && setAt(null), 200)
    }
    const down = (): void => {
      if (timer.current) clearTimeout(timer.current)
      setAt(null)
    }
    document.addEventListener('mouseover', over)
    document.addEventListener('mouseout', out)
    document.addEventListener('mousedown', down, true)
    return () => {
      document.removeEventListener('mouseover', over)
      document.removeEventListener('mouseout', out)
      document.removeEventListener('mousedown', down, true)
    }
  }, [store])

  if (!at) return null
  const n = store.g.get(at.id)
  if (!n || n.deletedAt !== null) return null
  const rows = visibleRows(store.g, n.id).filter((r) => r.depth < 3).slice(0, ROWS)
  const title = n.kind === 'journal' && n.date ? dayTitle(n.date).title : n.title
  const left = Math.min(at.x, window.innerWidth - 440)
  const style = at.y + 260 < window.innerHeight ? { left, top: at.y } : { left, top: at.y - 30, transform: 'translateY(-100%)' }
  return createPortal(
    <div
      className="preview"
      style={style}
      onMouseEnter={() => (inside.current = true)}
      onMouseLeave={() => {
        inside.current = false
        setAt(null)
      }}
    >
      {title ? <div className="preview-title">{title}</div> : <div className="preview-block">{<Content content={n.content} />}</div>}
      {rows.map((r) => (
        <div key={r.id} className="preview-row" style={{ paddingLeft: r.depth * 16 }}>
          <span className="dot" />
          <span>
            <Content content={store.g.must(r.id).content} depth={1} />
          </span>
        </div>
      ))}
      {rows.length === 0 && title && <div className="preview-empty">Empty page</div>}
    </div>,
    document.body,
  )
}
